import { t } from "../i18n";

/**
 * Client-side checks before upload. Mirrors server PDF rules; does not trust filename or browser MIME type (CWE-434).
 */

const MAX_DEFAULT_BYTES = 10 * 1024 * 1024;

function asciiPrefix(u8, s) {
  const enc = new TextEncoder().encode(s);
  if (u8.length < enc.length) return false;
  for (let i = 0; i < enc.length; i++) {
    if (u8[i] !== enc[i]) return false;
  }
  return true;
}

function hasPdfHeader(u8) {
  if (u8.length < 8) return false;
  if (!asciiPrefix(u8, "%PDF-")) return false;
  const major = String.fromCharCode(u8[5]);
  const dot = u8[6];
  const minor = String.fromCharCode(u8[7]);
  if (!/^[1-9]$/.test(major) || dot !== ".".charCodeAt(0) || !/^[0-9]$/.test(minor)) return false;
  return true;
}

function headLooksLikeHtmlPolyglot(u8) {
  const n = Math.min(u8.length, 512);
  const dec = new TextDecoder("latin1");
  const head = dec.decode(u8.subarray(0, n)).toLowerCase();
  return (
    head.includes("<!doctype html") || head.includes("<html") || head.includes("<script")
  );
}

/**
 * @param {File} file
 * @param {{ maxBytes?: number }} opts
 * @returns {Promise<{ ok: true } | { ok: false, message: string }>}
 */
export async function validateMerchantCompliancePdfFile(file, opts = {}) {
  const maxBytes = opts.maxBytes ?? MAX_DEFAULT_BYTES;
  if (!(file instanceof File)) return { ok: false, message: t("file.none_selected") };
  if (file.size <= 0) return { ok: false, message: t("file.empty") };
  if (file.size > maxBytes) {
    return { ok: false, message: t("file.pdf_too_large", { mb: Math.round(maxBytes / (1024 * 1024)) }) };
  }
  if (file.size < 32) return { ok: false, message: t("file.pdf_too_small") };

  let head;
  try {
    head = new Uint8Array(await file.slice(0, Math.min(file.size, 64)).arrayBuffer());
  } catch {
    return { ok: false, message: t("file.unreadable") };
  }

  if (!hasPdfHeader(head)) {
    return {
      ok: false,
      message: t("file.pdf_type"),
    };
  }
  if (headLooksLikeHtmlPolyglot(head)) {
    return { ok: false, message: t("file.pdf_invalid") };
  }

  const tailSize = Math.min(4096, file.size);
  let tail;
  try {
    tail = new Uint8Array(await file.slice(file.size - tailSize, file.size).arrayBuffer());
  } catch {
    return { ok: false, message: t("file.unreadable") };
  }
  const tailStr = new TextDecoder("latin1").decode(tail);
  if (!tailStr.includes("%%EOF")) {
    return { ok: false, message: t("file.pdf_incomplete") };
  }

  return { ok: true };
}
